import { useState, useEffect, useRef } from "react";
import { Globe } from "lucide-react";
import flagIsrael from "@/assets/flag-israel.png";
import flagUsa from "@/assets/flag-usa.png";
import flagRussia from "@/assets/flag-russia.png";
import flagPalestine from "@/assets/flag-palestine.png";
import { getTranslateLang, setTranslateLang } from "@/lib/translateUtils";

const TRANSLATE_LANGUAGES = [
  { code: "", label: "עברית", flag: flagIsrael, dir: "rtl" as const },
  { code: "en", label: "English", flag: flagUsa, dir: "ltr" as const },
  { code: "ru", label: "Русский", flag: flagRussia, dir: "ltr" as const },
  { code: "ar", label: "العربية", flag: flagPalestine, dir: "rtl" as const },
];

function setGoogleTranslateCookie(langCode: string) {
  const value = langCode ? `/he/${langCode}` : "/he/he";
  document.cookie = `googtrans=${value};path=/;`;
  document.cookie = `googtrans=${value};path=/;domain=${window.location.hostname}`;
}

function applyDirection(langCode: string) {
  const lang = TRANSLATE_LANGUAGES.find((l) => l.code === langCode) || TRANSLATE_LANGUAGES[0];
  const dir = lang.dir;
  
  const getStyleContent = () => {
    if (dir !== 'ltr') return '';
    const ruFontRule = langCode === 'ru' ? `body { font-size: 0.8125rem !important; }` : '';
    return `
      .lucide-chevron-left:not(.no-flip), .lucide-chevron-right:not(.no-flip) {
        transform: scaleX(-1) !important;
      }
      ${ruFontRule}
    `;
  };

  const apply = () => {
    // Set dir attribute on html
    document.documentElement.setAttribute('dir', dir);
    
    // Ensure injected style tag exists and has correct content
    let styleEl = document.getElementById('site-dir-override') as HTMLStyleElement;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'site-dir-override';
      (document.body || document.head).appendChild(styleEl);
    }
    styleEl.textContent = getStyleContent();
    
    // Override all hardcoded dir attributes on child elements
    document.querySelectorAll('[dir]').forEach((el) => {
      if (el === document.documentElement) return;
      if (el.getAttribute('dir') !== dir) {
        el.setAttribute('dir', dir);
      }
    });
  };
  
  apply();

  // Clear previous interval
  if ((window as any).__dirInterval) {
    clearInterval((window as any).__dirInterval);
  }
  // Disconnect previous child observer
  if ((window as any).__dirObserver) {
    (window as any).__dirObserver.disconnect();
  }
  
  // Persistent interval to fight Google Translate resetting attributes
  (window as any).__dirInterval = setInterval(apply, 500);
  // Also apply on shorter intervals initially
  [50, 150, 300, 800, 1500, 3000].forEach((ms) => setTimeout(apply, ms));
  
  // Watch for dynamically added elements with wrong dir
  if (dir === 'ltr') {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            if (node.getAttribute('dir') === 'rtl') {
              node.setAttribute('dir', 'ltr');
            }
            node.querySelectorAll('[dir="rtl"]').forEach((child) => {
              child.setAttribute('dir', 'ltr');
            });
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    (window as any).__dirObserver = observer;
  }
  
  // Stop interval when switching back to RTL (Hebrew)
  if (dir === 'rtl') {
    clearInterval((window as any).__dirInterval);
    (window as any).__dirInterval = null;
  }
}

export function triggerGoogleTranslate(langCode: string) {
  setTranslateLang(langCode);
  setGoogleTranslateCookie(langCode);
  applyDirection(langCode);

  // Update URL with lang parameter
  const url = new URL(window.location.href);
  if (langCode) {
    url.searchParams.set("lang", langCode);
  } else {
    url.searchParams.delete("lang");
  }
  window.history.replaceState({}, "", url.toString());

  if (!langCode) {
    // Switching back to Hebrew: remove Google Translate entirely
    // Clear the googtrans cookie
    document.cookie = "googtrans=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT";
    document.cookie = `googtrans=;path=/;domain=${window.location.hostname};expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    
    // Try to restore via combo box first
    const combo = document.querySelector<HTMLSelectElement>(".goog-te-combo");
    if (combo) {
      combo.value = "";
      combo.dispatchEvent(new Event("change"));
      // Give Google Translate a moment, then reload to fully restore
      setTimeout(() => window.location.reload(), 100);
      return;
    }
    
    // Fallback: reload to clear translation
    window.location.reload();
    return;
  }

  // Try to use the Google Translate combo box directly
  const combo = document.querySelector<HTMLSelectElement>(".goog-te-combo");
  if (combo) {
    combo.value = langCode;
    combo.dispatchEvent(new Event("change"));
    return;
  }

  // Fallback: reload to apply cookie
  window.location.reload();
}

function getCurrentLang(): string {
  // Check URL parameter first
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get("lang");
    if (urlLang && TRANSLATE_LANGUAGES.some((l) => l.code === urlLang)) {
      return urlLang;
    }
  }

  // Then check localStorage
  return getTranslateLang();
}

export function GoogleTranslateSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(getCurrentLang);
  const ref = useRef<HTMLDivElement>(null);

  const activeLang = TRANSLATE_LANGUAGES.find((l) => l.code === currentLang) || TRANSLATE_LANGUAGES[0];

  // On mount: apply direction and handle URL param
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get("lang");
    
    if (urlLang && TRANSLATE_LANGUAGES.some((l) => l.code === urlLang)) {
      setCurrentLang(urlLang);
      triggerGoogleTranslate(urlLang);
      
      // Clean up URL param
      const url = new URL(window.location.href);
      url.searchParams.delete("lang");
      window.history.replaceState({}, "", url.toString());
    } else {
      applyDirection(currentLang);
      if (currentLang) {
        setGoogleTranslateCookie(currentLang);
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (code: string) => {
    setCurrentLang(code);
    setIsOpen(false);
    triggerGoogleTranslate(code);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-muted/50 transition-colors"
        aria-label="בחר שפה"
      >
        <img
          src={activeLang.flag}
          alt={activeLang.label}
          className="w-5 h-4 object-cover rounded-sm"
        />
        <Globe className="h-4 w-4 text-primary" strokeWidth={1.5} />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1 bg-background border border-border rounded-lg shadow-lg z-[9999] min-w-[150px] py-1 overflow-hidden">
          {TRANSLATE_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-muted/80 ${
                currentLang === lang.code
                  ? "bg-primary/10 font-semibold text-primary"
                  : "text-foreground"
              }`}
            >
              <img
                src={lang.flag}
                alt={lang.label}
                className="w-6 h-4 object-cover rounded-sm border border-border/30"
              />
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
